import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { McpServerInput } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Button, Card, ErrorText, Spinner } from "../components/ui.tsx";
import { ConnectionBadge, HeadersDialog, ServerDialog, ServerRow, ToolsDialog, useOAuthResult, type McpServer } from "../components/mcp.tsx";

/**
 * A person's MCP servers: the company's (under Connected apps → Apps, to connect their own account where
 * needed) or their own (under Developer). A sign-in returning from a server's page shows its result on either.
 */
export function McpServers({ part }: { part: "company" | "own" }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-mcp"], queryFn: () => unwrap(api.me.mcp.$get()) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["my-mcp"] });
  const oauth = useOAuthResult();
  const [editing, setEditing] = useState<McpServer | null | "new">(null);
  const [headersFor, setHeadersFor] = useState<McpServer | null>(null);
  const [testing, setTesting] = useState<McpServer | null>(null);
  const [connected, setConnected] = useState<string | null>(oauth.connected);

  const connect = useMutation({
    mutationFn: async ({ s, headers }: { s: McpServer; headers?: Record<string, string> }) => unwrap(api.me.mcp[":id"].connect.$post({ param: { id: s.id }, json: { headers } })),
    onSuccess: (r, { s }) => {
      if (r.authorizationUrl) location.href = r.authorizationUrl;
      else {
        setConnected(s.name);
        refresh();
      }
    },
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => unwrap(api.me.mcp[":id"].connection.$delete({ param: { id } })),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.me.mcp[":id"].$delete({ param: { id } })),
    onSuccess: refresh,
  });
  const restart = useMutation({ mutationFn: () => unwrap(api.me.runtime.restart.$post()) });
  const runTest = useCallback((id: string) => unwrap(api.me.mcp[":id"].test.$post({ param: { id } })), []);

  const save = async (input: McpServerInput, headerValues: Record<string, string> | null) => {
    const s = (editing && editing !== "new"
      ? await unwrap(api.me.mcp[":id"].$put({ param: { id: editing.id }, json: input }))
      : await unwrap(api.me.mcp.$post({ json: input }))) as McpServer;
    refresh();
    if (headerValues) await connect.mutateAsync({ s, headers: headerValues });
    // A new OAuth server: go and sign in straight away.
    else if (s.auth === "oauth" && s.connection?.status !== "connected") await connect.mutateAsync({ s });
  };

  /** Connect: header values in a dialog, or off to the server's sign-in page. */
  const startConnect = (s: McpServer) => (s.auth === "headers" && s.headerNames.length ? setHeadersFor(s) : connect.mutate({ s }));

  const actions = (s: McpServer, own: boolean) => {
    const mine = own || s.credentials === "personal";
    const st = s.connection?.status;
    return (
      <>
        {s.connection && mine && st !== "connected" && (
          <Button className="h-8" variant="primary" disabled={connect.isPending} onClick={() => startConnect(s)}>
            {connect.isPending && connect.variables?.s.id === s.id ? <Spinner /> : st === "expired" || st === "error" ? "Reconnect" : "Connect"}
          </Button>
        )}
        {(!s.connection || st === "connected" || !mine) && (
          <Button className="h-8" variant="ghost" onClick={() => setTesting(s)} disabled={!!s.connection && st !== "connected"}>Test</Button>
        )}
        {own && <Button className="h-8" variant="ghost" onClick={() => setEditing(s)}>Edit</Button>}
        {s.connection && mine && st === "connected" && (
          <Button className="h-8" variant="ghost" onClick={() => confirm(`Disconnect your account from ${s.name}?`) && disconnect.mutate(s.id)}>Disconnect</Button>
        )}
        {own && (
          <Button className="h-8" variant="ghost" onClick={() => confirm(`Remove ${s.name}? Your assistant restarts briefly without it.`) && remove.mutate(s.id)}>Remove</Button>
        )}
      </>
    );
  };

  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const { company, mine, canAdd } = q.data as { company: McpServer[]; mine: McpServer[]; canAdd: boolean };
  const result = connected || oauth.error;
  // Most people have no company servers: then the Apps tab doesn't mention MCP at all.
  if (part === "company" && !company.length && !result) return null;

  return (
    <>
      {result && (
        <Card className={connected ? "mb-4 flex flex-wrap items-center justify-between gap-3 border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800" : "mb-4 border-red-300 bg-red-50 p-4 text-sm text-red-800"}>
          {connected ? (
            <>
              <span>Connected {connected}. Your assistant picks it up within a few minutes, or restart it to use it now.</span>
              <Button className="h-8" disabled={restart.isPending || restart.isSuccess} onClick={() => restart.mutate()}>
                {restart.isSuccess ? "Restarting…" : "Restart assistant"}
              </Button>
            </>
          ) : (
            <>Couldn't connect: {oauth.error}</>
          )}
        </Card>
      )}
      <ErrorText error={connect.error ?? disconnect.error ?? remove.error ?? restart.error} />

      {part === "company" ? (
        !!company.length && (
          <>
            <h2 className="mt-2 mb-1 text-base font-semibold">Company tools (MCP servers)</h2>
            <p className="mb-3 text-sm text-[var(--muted)]">Tools your admins added for everyone. Where one asks for your own account, connect it here; keys and sign-ins stay with us, never with the assistant.</p>
            <Card className="mb-8 divide-y divide-[var(--line)]">
              {company.map((s) => (
                <ServerRow key={s.id} s={s} badge={<ConnectionBadge s={s} sharedHint={s.credentials === "shared" ? "waiting for an admin" : undefined} />} actions={actions(s, false)} />
              ))}
            </Card>
          </>
        )
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold">Your MCP servers</h2>
              <p className="text-sm text-[var(--muted)]">Private servers only your assistant uses. Keys and sign-ins stay with us, never with the assistant.</p>
            </div>
            {canAdd && <Button variant="primary" onClick={() => setEditing("new")}>Add a server</Button>}
          </div>
          {!canAdd ? (
            <Card className="mb-8 p-6 text-sm text-[var(--muted)]">Your group can't add its own MCP servers. Ask an admin if you need one.</Card>
          ) : (
            <Card className="mb-8 divide-y divide-[var(--line)]">
              {mine.length === 0 && <p className="px-5 py-8 text-center text-sm text-[var(--muted)]">None yet. Add any MCP server with a public https URL; only your assistant can use it.</p>}
              {mine.map((s) => (
                <ServerRow key={s.id} s={s} badge={<ConnectionBadge s={s} />} actions={actions(s, true)} />
              ))}
            </Card>
          )}
        </>
      )}

      <ServerDialog open={editing !== null} onClose={() => setEditing(null)} company={false} initial={editing === "new" ? null : editing} onSave={save} />
      <HeadersDialog server={headersFor} onClose={() => setHeadersFor(null)} onSubmit={async (headers) => void (await connect.mutateAsync({ s: headersFor!, headers }))} />
      <ToolsDialog server={testing} onClose={() => setTesting(null)} run={runTest} />
    </>
  );
}
